import React, { useState, useRef, useMemo } from 'react';
import { useQuery, useLazyQuery, useMutation, useApolloClient } from '@apollo/client';
import { Chip, ExternalChip, ChipTab } from '../../lib/types';
import {
  mapMiniChipToRescueChip,
  mapResellerChipToRescueChip,
  mapPetFormToChipNewPetInput,
  mapAdopterFormToContactInfoInput,
} from '../../lib/chipAdapters';
import { useToast } from '../../components/Toast/ToastContext';
import { ChipStatusType } from '@/types/gql-op-types';
import {
  rescueViewingChipsQuery,
  RescueViewingChipsData,
  rescueSearchChipsQuery,
  RescueSearchChipsData,
} from '../../lib/rescueQueries';
import {
  resellerChipDetailsQuery,
  registerThirdPartyChip,
  recallChipMutation,
  reassignChipToResellerMutation,
} from '@/appview/ResellerChipDetails/ResellerChipDetails.graphql';
import {
  assignNewPetToChipMutation,
  assignNewOwnerToChipMutation,
} from '@/appview/RegisterChipV2/RegisterChip.graphql';
import SearchBar from '../../components/SearchBar/SearchBar';
import PortalTabs, { TabDef } from '../../components/PortalTabs/PortalTabs';
import Button from '../../components/Button/Button';
import ChipsTable from './ChipsTable';
import RegisterChipModal, { PetFormData } from './RegisterChipModal';
import TransferChipModal, { AdopterFormData } from './TransferChipModal';
import ChipDetailModal from './ChipDetailModal';
import RecallModal from './RecallModal';
import OrderChipsModal from './OrderChipsModal';
import PetLookupModal from './PetLookupModal';
import ExternalChipDetailModal from './ExternalChipDetailModal';
import ClaimChipModal from './ClaimChipModal';
import ReassignChipModal from './ReassignChipModal';
import Pagination from '../../components/Pagination/Pagination';
import {
  trackChipRegistrationStarted,
  trackChipRegistered,
  trackChipOwnerAdded,
  trackChipsOrderSubmitted,
  trackPetLookupSearched,
} from '../../analytics';
import styles from './Chips.module.scss';

const PAGE_SIZE = 20;
const SEARCH_DEBOUNCE_MS = 300;
const MIN_SEARCH_LENGTH = 3;

const refetchChipLists = [
  { query: rescueViewingChipsQuery, variables: { statusType: ChipStatusType.SHIPPED, limit: PAGE_SIZE, offset: 0 } },
  { query: rescueViewingChipsQuery, variables: { statusType: ChipStatusType.ACTIVE, limit: PAGE_SIZE, offset: 0 } },
];

function LoadingSpinner({ message }: { message?: string }) {
  return (
    <div className={styles.loadingContainer}>
      <svg className={styles.spinner} xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24">
        <circle cx="12" cy="12" r="10" stroke="currentColor" strokeWidth="3" opacity="0.25" />
        <path fill="currentColor" d="M4 12a8 8 0 018-8v3a5 5 0 00-5 5H4z" />
      </svg>
      {message && <span>{message}</span>}
    </div>
  );
}

export default function Chips() {
  const apolloClient = useApolloClient();
  const { showToast } = useToast();
  const [currentTab, setCurrentTab] = useState<ChipTab>('stock');
  const [searchQuery, setSearchQuery] = useState('');
  const [debouncedQuery, setDebouncedQuery] = useState('');
  const debounceRef = useRef<ReturnType<typeof setTimeout>>();
  const [stockPage, setStockPage] = useState(1);
  const [registeredPage, setRegisteredPage] = useState(1);

  function handleSearchChange(value: string) {
    setSearchQuery(value);
    clearTimeout(debounceRef.current);
    debounceRef.current = setTimeout(() => {
      setDebouncedQuery(value.trim());
    }, SEARCH_DEBOUNCE_MS);
  }

  // ── GraphQL queries ─────────────────────────

  const {
    data: shippedRawData,
    previousData: shippedPreviousData,
    loading: shippedLoading,
    error: shippedError,
  } = useQuery<RescueViewingChipsData>(rescueViewingChipsQuery, {
    variables: { statusType: ChipStatusType.SHIPPED, limit: PAGE_SIZE, offset: (stockPage - 1) * PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  });

  const {
    data: activeRawData,
    previousData: activePreviousData,
    loading: activeLoading,
    error: activeError,
  } = useQuery<RescueViewingChipsData>(rescueViewingChipsQuery, {
    variables: { statusType: ChipStatusType.ACTIVE, limit: PAGE_SIZE, offset: (registeredPage - 1) * PAGE_SIZE },
    fetchPolicy: 'cache-and-network',
  });

  // Keep stale data visible during pagination transitions.
  // When offset changes (cache miss), data becomes undefined while the network
  // request is in flight.  previousData retains the old rows so the table
  // never flashes empty.
  const shippedData = shippedRawData ?? shippedPreviousData;
  const activeData = activeRawData ?? activePreviousData;

  const isSearchActive = debouncedQuery.length >= MIN_SEARCH_LENGTH;
  const searchInput = useMemo(() => ({ searchTerm: debouncedQuery }), [debouncedQuery]);
  const { data: searchData, loading: searchLoading } = useQuery<RescueSearchChipsData>(rescueSearchChipsQuery, {
    variables: { input: searchInput },
    skip: !isSearchActive,
    fetchPolicy: 'cache-and-network',
  });

  const [fetchChipDetails] = useLazyQuery(resellerChipDetailsQuery);

  // ── GraphQL mutations ─────────────────────────

  const [assignNewPet] = useMutation(assignNewPetToChipMutation, { refetchQueries: refetchChipLists });
  const [assignNewOwner] = useMutation(assignNewOwnerToChipMutation, { refetchQueries: refetchChipLists });
  const [recallChipMut] = useMutation(recallChipMutation, { refetchQueries: refetchChipLists });
  const [registerThirdParty] = useMutation(registerThirdPartyChip, { refetchQueries: refetchChipLists });
  const [reassignToReseller] = useMutation(reassignChipToResellerMutation, { refetchQueries: refetchChipLists });

  // ── Derived chip lists ─────────────────────────
  // SHIPPED → stock tab, ACTIVE → registered tab.

  const resellerEmail =
    searchData?.currentUser?.email ?? shippedData?.currentUser?.email ?? activeData?.currentUser?.email ?? '';

  const stockChips = useMemo(() => {
    const chips = shippedData?.currentUser?.chipReseller?.chips?.chips ?? [];
    return chips.map((c) => mapMiniChipToRescueChip(c, resellerEmail));
  }, [shippedData, resellerEmail]);

  const registeredChips = useMemo(() => {
    const chips = activeData?.currentUser?.chipReseller?.chips?.chips ?? [];
    return chips.map((c) => mapMiniChipToRescueChip(c, resellerEmail));
  }, [activeData, resellerEmail]);

  const searchChips = useMemo(() => {
    const chips = searchData?.currentUser?.chipReseller?.searchChips?.chips ?? [];
    return chips.map((c) => mapMiniChipToRescueChip(c, resellerEmail));
  }, [searchData, resellerEmail]);

  const displayedChips = useMemo(() => {
    if (isSearchActive) {
      return searchChips;
    }
    return currentTab === 'stock' ? stockChips : registeredChips;
  }, [isSearchActive, searchChips, currentTab, stockChips, registeredChips]);

  const shippedTotal = shippedData?.currentUser?.chipReseller?.chips?.metadata?.totalChipCount ?? 0;
  const activeTotal = activeData?.currentUser?.chipReseller?.chips?.metadata?.totalChipCount ?? 0;

  const defaultTabs: TabDef[] = [
    { key: 'stock', label: 'In Stock', count: shippedTotal },
    { key: 'registered', label: 'Registered', count: activeTotal },
  ];
  const searchTabs: TabDef[] = [{ key: 'all', label: 'All', count: searchChips.length }];
  const tabs = isSearchActive ? searchTabs : defaultTabs;

  const isLoading = isSearchActive
    ? searchLoading
    : (currentTab === 'stock' && shippedLoading) || (currentTab === 'registered' && activeLoading);
  const queryError = isSearchActive ? undefined : currentTab === 'stock' ? shippedError : activeError;

  // Only show full-page spinner on true initial load (no data at all).
  // During pagination transitions, stale data is kept visible via previousData.
  const initialLoading = isLoading && !shippedData && !activeData && !isSearchActive;
  // Show the dim overlay when we have stale rows and a network request is in flight.
  const isTransitioning =
    isLoading &&
    !initialLoading &&
    (isSearchActive || (currentTab === 'stock' && !shippedRawData) || (currentTab === 'registered' && !activeRawData));

  const currentPage = currentTab === 'stock' ? stockPage : registeredPage;
  const currentTotal = currentTab === 'stock' ? shippedTotal : activeTotal;
  const totalPages = Math.ceil(currentTotal / PAGE_SIZE);

  function handlePageChange(page: number) {
    if (currentTab === 'stock') setStockPage(page);
    else setRegisteredPage(page);
  }

  function handleTabChange(key: string) {
    setCurrentTab(key as ChipTab);
    setSearchQuery('');
  }

  // Modal state
  const [registerOpen, setRegisterOpen] = useState(false);
  const [registerInitialId, setRegisterInitialId] = useState('');
  const [transferOpen, setTransferOpen] = useState(false);
  const [transferChip, setTransferChip] = useState<Chip | null>(null);
  const [detailOpen, setDetailOpen] = useState(false);
  const [detailChip, setDetailChip] = useState<Chip | null>(null);
  const [detailLoading, setDetailLoading] = useState(false);
  const [recallOpen, setRecallOpen] = useState(false);
  const [recallChipState, setRecallChipState] = useState<Chip | null>(null);
  const [orderOpen, setOrderOpen] = useState(false);
  const [lookupOpen, setLookupOpen] = useState(false);
  const [extDetailOpen, setExtDetailOpen] = useState(false);
  const [extDetailChip, setExtDetailChip] = useState<ExternalChip | null>(null);
  const [claimOpen, setClaimOpen] = useState(false);
  const [claimChip, setClaimChip] = useState<ExternalChip | null>(null);
  const [reassignOpen, setReassignOpen] = useState(false);
  const [reassignChip, setReassignChip] = useState<ExternalChip | null>(null);

  // ── Modal handlers ──────────────────────────

  function handleRegister(chipId: string) {
    setRegisterInitialId(chipId);
    setRegisterOpen(true);
    trackChipRegistrationStarted(chipId ? 'stock_tab' : 'header_button');
  }

  async function handleRegisterSubmit(
    graphqlId: string,
    chipId: string,
    serialNumber: string,
    needsClaim: boolean,
    petData?: PetFormData,
    breedId?: string | null,
    adopterData?: AdopterFormData,
  ) {
    if (needsClaim) {
      const { data } = await reassignToReseller({
        variables: { input: { serialNumber, clearOwnerInfo: false } },
      });
      // Clear just the owner while preserving pet info
      const reassignedId = data?.reassignChipToReseller?.id;
      if (reassignedId) {
        await recallChipMut({ variables: { input: { chipId: reassignedId } } });
      }
    }

    if (petData) {
      await assignNewPet({
        variables: {
          input: {
            chipId: graphqlId,
            petInfo: mapPetFormToChipNewPetInput(petData, breedId),
          },
        },
      });
    }

    if (adopterData) {
      await assignNewOwner({
        variables: {
          input: {
            chipId: graphqlId,
            contactInfo: mapAdopterFormToContactInfoInput(adopterData),
          },
        },
      });
      trackChipRegistered(!!petData, petData?.species);
      showToast(`Chip ${chipId} registered with owner successfully`);
    } else {
      trackChipRegistered(false, petData?.species);
      showToast(`Chip ${chipId} registered successfully`);
    }
  }

  async function handleDetail(chip: Chip) {
    setDetailChip(chip);
    setDetailOpen(true);
    setDetailLoading(true);

    try {
      const { data } = await fetchChipDetails({ variables: { id: chip._graphqlId } });
      if (data?.resellerChip) {
        const email = data.currentUser?.email ?? resellerEmail;
        setDetailChip(mapResellerChipToRescueChip(data.resellerChip, email));
      }
    } catch {
      // Keep showing the mini chip data we already have
    } finally {
      setDetailLoading(false);
    }
  }

  function handleTransfer(chip: Chip) {
    setTransferChip(chip);
    setTransferOpen(true);
  }

  async function handleTransferSubmit(chipId: string, adopter: AdopterFormData) {
    const allChips = [...stockChips, ...registeredChips];
    const chip = allChips.find((c) => c.id === chipId);
    if (!chip) return;

    try {
      await assignNewOwner({
        variables: {
          input: {
            chipId: chip._graphqlId,
            contactInfo: mapAdopterFormToContactInfoInput(adopter),
          },
        },
      });
      trackChipOwnerAdded();
      showToast(`Chip ${chipId} transferred successfully`);
    } catch (err) {
      showToast(`Failed to transfer chip: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleRecall(chip: Chip) {
    setRecallChipState(chip);
    setRecallOpen(true);
  }

  async function handleRecallSubmit(chipId: string) {
    const allChips = [...stockChips, ...registeredChips];
    const chip = allChips.find((c) => c.id === chipId);
    if (!chip) return;

    try {
      await recallChipMut({
        variables: { input: { chipId: chip._graphqlId } },
      });
      showToast(`Chip ${chipId} recalled successfully`);
    } catch (err) {
      showToast(`Failed to recall chip: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handlePetLookup() {
    setLookupOpen(true);
  }

  function handleAddedToStock() {
    showToast('Chip added to stock successfully');
    // Refetch chip lists to show the newly added chip
    for (const q of refetchChipLists) {
      apolloClient.refetchQueries({ include: [q.query] });
    }
  }

  function handleOrderChips() {
    setOrderOpen(true);
  }

  function handleViewExternal(chip: ExternalChip) {
    setExtDetailChip(chip);
    setExtDetailOpen(true);
  }

  async function handleViewOwnFromLookup(graphqlId: string) {
    setDetailChip(null);
    setDetailOpen(true);
    setDetailLoading(true);

    try {
      const { data } = await fetchChipDetails({ variables: { id: graphqlId } });
      if (data?.resellerChip) {
        const email = data.currentUser?.email ?? resellerEmail;
        setDetailChip(mapResellerChipToRescueChip(data.resellerChip, email));
      }
    } catch {
      // Failed to load details
    } finally {
      setDetailLoading(false);
    }
  }

  function handleClaim(chip: ExternalChip) {
    setClaimChip(chip);
    setClaimOpen(true);
  }

  async function handleClaimSubmit(chip: ExternalChip) {
    try {
      // Step 1: Reassign chip to our org (keep pet + owner so we get the graphql ID back)
      const { data } = await reassignToReseller({
        variables: {
          input: {
            serialNumber: chip.fullChipId,
            clearOwnerInfo: false,
          },
        },
      });
      // Step 2: Clear just the owner (recallChip preserves pet info)
      const reassignedId = data?.reassignChipToReseller?.id;
      if (reassignedId) {
        await recallChipMut({ variables: { input: { chipId: reassignedId } } });
      }
      showToast(`Chip ${chip.id} claimed successfully`);
    } catch (err) {
      showToast(`Failed to claim chip: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  function handleReassign(chip: ExternalChip) {
    setReassignChip(chip);
    setReassignOpen(true);
  }

  async function handleReassignSubmit(chip: ExternalChip, keepOwner: boolean) {
    try {
      await reassignToReseller({
        variables: {
          input: {
            serialNumber: chip.fullChipId,
            clearOwnerInfo: !keepOwner,
          },
        },
      });
      showToast(`Chip ${chip.id} reassigned successfully`);
    } catch (err) {
      showToast(`Failed to reassign chip: ${err instanceof Error ? err.message : 'Unknown error'}`);
    }
  }

  return (
    <div>
      {/* Page header */}
      <div className={styles.pageHeader}>
        <h1>Nano Microchips</h1>
        <div className={styles.headerActions}>
          <Button variant="ghost" onClick={handlePetLookup}>
            <svg
              className={styles.headerBtnIcon}
              xmlns="http://www.w3.org/2000/svg"
              viewBox="0 0 24 24"
              fill="none"
              stroke="currentColor"
              strokeWidth="2"
              strokeLinecap="round"
              strokeLinejoin="round"
            >
              <circle cx="11" cy="11" r="8" />
              <line x1="21" y1="21" x2="16.65" y2="16.65" />
            </svg>
            Fi Nano Registry
          </Button>
          <div className={styles.orderNanosWrapper}>
            <Button variant="green" onClick={handleOrderChips}>
              <svg
                className={styles.headerBtnIcon}
                xmlns="http://www.w3.org/2000/svg"
                viewBox="0 0 24 24"
                fill="none"
                stroke="currentColor"
                strokeWidth="2"
                strokeLinecap="round"
                strokeLinejoin="round"
              >
                <circle cx="9" cy="21" r="1" />
                <circle cx="20" cy="21" r="1" />
                <path d="M1 1h4l2.68 13.39a2 2 0 0 0 2 1.61h9.72a2 2 0 0 0 2-1.61L23 6H6" />
              </svg>
              Order Nanos
            </Button>
            <span className={styles.orderNanosSubtitle}>Free for all rescues</span>
          </div>
        </div>
      </div>

      {/* Search */}
      <SearchBar
        value={searchQuery}
        onChange={handleSearchChange}
        placeholder="Search by chip ID, pet name, or owner..."
      />

      {/* Tabs + Register Chip */}
      <div className={styles.tabsRow}>
        <PortalTabs tabs={tabs} activeTab={isSearchActive ? 'all' : currentTab} onTabChange={handleTabChange} />
        <Button variant="dark" onClick={() => handleRegister('')}>
          <svg
            className={styles.headerBtnIcon}
            xmlns="http://www.w3.org/2000/svg"
            viewBox="0 0 24 24"
            fill="none"
            stroke="currentColor"
            strokeWidth="2.5"
            strokeLinecap="round"
            strokeLinejoin="round"
          >
            <line x1="12" y1="5" x2="12" y2="19" />
            <line x1="5" y1="12" x2="19" y2="12" />
          </svg>
          Register Chip
        </Button>
      </div>

      {/* Content */}
      {initialLoading ? (
        <LoadingSpinner message="Loading chips..." />
      ) : queryError && !shippedData && !activeData ? (
        <div className={styles.errorContainer}>
          <p>Failed to load chips. Please try again.</p>
        </div>
      ) : (
        <>
          <div className={isTransitioning ? styles.tableTransitioning : undefined}>
            <ChipsTable
              chips={displayedChips}
              tab={isSearchActive ? 'all' : currentTab}
              searchQuery={searchQuery}
              onRegister={handleRegister}
              onDetail={handleDetail}
              onTransfer={handleTransfer}
              onRecall={handleRecall}
            />
          </div>
          {!isSearchActive && (
            <Pagination
              currentPage={currentPage}
              totalPages={totalPages}
              totalItems={currentTotal}
              pageSize={PAGE_SIZE}
              onPageChange={handlePageChange}
            />
          )}
        </>
      )}

      {/* Info banner */}
      <div className={styles.infoBanner}>
        <svg
          className={styles.infoBannerIcon}
          xmlns="http://www.w3.org/2000/svg"
          width="18"
          height="18"
          viewBox="0 0 24 24"
          fill="none"
          stroke="currentColor"
          strokeWidth="2"
          strokeLinecap="round"
          strokeLinejoin="round"
        >
          <circle cx="12" cy="12" r="10" />
          <line x1="12" y1="16" x2="12" y2="12" />
          <line x1="12" y1="8" x2="12.01" y2="8" />
        </svg>
        <span>
          Fi Nano microchips can be re-ordered for free by Fi rescue partners.{' '}
          <a href="/rescue/help" className={styles.infoBannerLink}>
            Learn more
          </a>
        </span>
      </div>

      {/* Modals */}
      <RegisterChipModal
        key={registerInitialId}
        open={registerOpen}
        onClose={() => setRegisterOpen(false)}
        initialChipId={registerInitialId}
        onRegister={handleRegisterSubmit}
      />
      <TransferChipModal
        open={transferOpen}
        onClose={() => setTransferOpen(false)}
        chip={transferChip}
        onTransfer={handleTransferSubmit}
      />
      <ChipDetailModal
        open={detailOpen}
        onClose={() => setDetailOpen(false)}
        chip={detailChip}
        loading={detailLoading}
        onTransfer={handleTransfer}
        onRecall={handleRecall}
      />
      <RecallModal
        open={recallOpen}
        onClose={() => setRecallOpen(false)}
        chip={recallChipState}
        onRecall={handleRecallSubmit}
      />
      <OrderChipsModal open={orderOpen} onClose={() => setOrderOpen(false)} />
      <PetLookupModal
        open={lookupOpen}
        onClose={() => setLookupOpen(false)}
        onViewExternal={handleViewExternal}
        onViewOwn={handleViewOwnFromLookup}
        onAddedToStock={handleAddedToStock}
      />
      <ExternalChipDetailModal
        open={extDetailOpen}
        onClose={() => setExtDetailOpen(false)}
        chip={extDetailChip}
        onClaim={handleClaim}
        onReassign={handleReassign}
      />
      <ClaimChipModal
        open={claimOpen}
        onClose={() => setClaimOpen(false)}
        chip={claimChip}
        onClaim={handleClaimSubmit}
      />
      <ReassignChipModal
        open={reassignOpen}
        onClose={() => setReassignOpen(false)}
        chip={reassignChip}
        onReassign={handleReassignSubmit}
      />
    </div>
  );
}
